import { useState, useEffect, useMemo } from "react";

import { calculateQuickMatch } from "../utils/calculateMatchScore";
import { getSpecsMap } from "../utils/productSpecs";
import { capMatchPercent } from "../utils/matchScoreUtils";
import { useUserAuth } from "../contexts/UserAuthContext";
import {
  fetchProductMatchExplanation,
} from "../utils/fetchProductMatchExplanation";
import useHomeChatMatchSession from "./useHomeChatMatchSession";
import { buildAiSessionMatchPayload } from "../utils/resolveProductMatch";
import { hasMatchIntent, resolveMatchIntent } from "../utils/resolveMatchIntent";
import { getAiSessionDependencyKey } from "../utils/homeChatMatchPersistence";
import useCategoryDetails from "./useCategoryDetails";

const getProductId = (product) => {
  if (!product || typeof product === "string") return null;
  return product._id || product.id || null;
};

const buildSpecsForMatch = (product, categoryFields) => {
  if (
    Array.isArray(product.specifications) &&
    product.specifications.length
  ) {
    return product.specifications
      .filter(
        (s) =>
          s?.value !== undefined && s?.value !== null && s?.value !== "",
      )
      .map((s) => ({
        key: s.key || s.label,
        label: s.label || s.key,
        value: s.value,
      }));
  }

  const specsMap = getSpecsMap(product);
  const fromCategory =
    (Array.isArray(categoryFields) ? categoryFields : [])
      .map((f) => {
        const v =
          specsMap?.[f.name] ??
          specsMap?.[f.name?.toLowerCase?.()] ??
          product?.[f.name] ??
          product?.[f.name?.toLowerCase?.()] ??
          undefined;
        if (v === undefined || v === null || v === "") return null;
        return {
          key: f.name,
          label: f.label || f.name,
          value: v,
        };
      })
      .filter(Boolean) || [];

  const base = [
    {
      key: "brand",
      label: "Brand",
      value: product?.brand?.name || product?.brand,
    },
    { key: "model", label: "Model", value: product?.model },
    { key: "year", label: "Year", value: product?.year },
  ].filter((x) => x.value);

  const specs = [...fromCategory, ...base];
  const seen = new Set();
  return specs.filter((s) => {
    const id = (s.key || s.label || "").toLowerCase();
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Returns the user's match score (0–97) for a product, or null if unavailable.
 * Logged-in: Shopper Model via match-explanation (empty body).
 * Guest: match-explanation with localStorage chat answers, else quick client calc.
 */
export const useProductMatchScore = (product, options = {}) => {
  const {
    preferExplanationOnly = false,
    wizardRevision = 0,
    // When the parent already resolved this product's score via a batched
    // request (see useProductGridMatchScores), pass it here so this hook
    // skips its own per-product network call entirely.
    precomputed = null,
  } = options;
  const { user } = useUserAuth();
  const aiSession = useHomeChatMatchSession(user, { syncServer: false });
  const sessionKey = useMemo(
    () => getAiSessionDependencyKey(aiSession),
    [aiSession],
  );
  const [enhancedMatchScore, setEnhancedMatchScore] = useState(null);
  const [lowConfidence, setLowConfidence] = useState(false);

  const productId = getProductId(product);
  const userId = user?.id || user?._id || null;
  const aiSessionMatch = useMemo(
    () => buildAiSessionMatchPayload(product, aiSession),
    [product, sessionKey],
  );
  const resolvedCategoryId =
    (typeof product?.category === "object"
      ? product.category._id
      : product?.category) || null;

  const { fields, allFields } = useCategoryDetails(resolvedCategoryId);
  const categoryFields = allFields || fields;

  useEffect(() => {
    if (!productId) {
      setEnhancedMatchScore(null);
      setLowConfidence(false);
      return;
    }

    if (aiSessionMatch || product?.aiRanked) {
      setEnhancedMatchScore(null);
      setLowConfidence(false);
      return;
    }

    const guestIntent = resolveMatchIntent({ aiSession, user: null });
    if (!user && !hasMatchIntent(guestIntent.wizardAnswers)) {
      setEnhancedMatchScore(null);
      setLowConfidence(false);
      return;
    }

    // A parent grid is resolving scores for all its cards in one batched
    // request (see useProductGridMatchScores) — wait for it instead of
    // firing a redundant per-card network call.
    if (precomputed?.enabled) {
      if (precomputed.ready) {
        setEnhancedMatchScore(
          precomputed.score != null ? precomputed.score : null,
        );
        setLowConfidence(precomputed.lowConfidence || false);
      }
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      const result = await fetchProductMatchExplanation(productId, {
        user,
        userId,
        aiSession,
      });
      if (cancelled) return;
      if (result?.score != null) {
        setEnhancedMatchScore(result.score);
        setLowConfidence(result.lowConfidence || false);
      } else {
        setEnhancedMatchScore(null);
        setLowConfidence(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    productId,
    userId,
    wizardRevision,
    user,
    product?.aiRanked,
    aiSessionMatch,
    sessionKey,
    aiSession,
    precomputed?.enabled,
    precomputed?.ready,
    precomputed?.score,
    precomputed?.lowConfidence,
  ]);

  const matchScore = useMemo(() => {
    if (!product || typeof product === "string") return null;

    if (lowConfidence && preferExplanationOnly) {
      return null;
    }

    if (aiSessionMatch?.score) {
      return aiSessionMatch.score;
    }

    if (product.aiRanked) {
      const aiScore =
        product?.matchScore ?? product?.score ?? product?.match ?? null;
      if (aiScore !== null && aiScore !== undefined && aiScore > 0) {
        return capMatchPercent(aiScore);
      }
    }

    if (enhancedMatchScore !== null) {
      return enhancedMatchScore;
    }

    if (!preferExplanationOnly) {
      const existingScore =
        product?.matchScore ?? product?.score ?? product?.match ?? null;
      // Treat 0 as no score — it likely means the engine had empty weights or
      // missing match intent data rather than a genuine "0% match".
      if (existingScore !== null && existingScore !== undefined && existingScore > 0) {
        return capMatchPercent(existingScore);
      }
    }

    if (user) return null;

    const { wizardAnswers } = resolveMatchIntent({ aiSession, user: null });
    if (!wizardAnswers) return null;

    try {
      const specs = buildSpecsForMatch(product, categoryFields);
      return calculateQuickMatch(product, wizardAnswers, specs);
    } catch {
      return null;
    }
  }, [
    product,
    categoryFields,
    enhancedMatchScore,
    preferExplanationOnly,
    user,
    lowConfidence,
    aiSessionMatch,
    sessionKey,
  ]);

  return matchScore;
};

export default useProductMatchScore;
